import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../../config/ApiConfig';
import {
  formatCurrencyInput,
  parseCurrencyInput,
} from '../../utils/currencyUtils';

function ReceitaModal({ show, onClose, onSaveSuccess, receita = null }) {
  const isEditing = !!receita;

  const [formData, setFormData] = useState({
    ano: new Date().getFullYear(),
    cnpjAssociacao: '',
    numConta: '',
    id: '',
    idPrograma: '',
    idFonteRecurso: '',
    data: '',
    valor: '',
    descricao: '',
  });

  const [contas, setContas] = useState([]);
  const [programas, setProgramas] = useState([]);
  const [fontesRecurso, setFontesRecurso] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const token = localStorage.getItem('token');
  const cnpj = localStorage.getItem('cnpj');
  const anoBase =
    parseInt(localStorage.getItem('anoBase')) || new Date().getFullYear();

  // Resetar formulário quando o modal abrir/fechar
  useEffect(() => {
    if (show) {
      if (isEditing && receita) {
        setFormData({
          ano: receita.ano || anoBase,
          cnpjAssociacao: receita.cnpjAssociacao || cnpj,
          numConta: receita.numConta || '',
          id: receita.id || '',
          idPrograma: receita.idPrograma || receita.programa?.id || '',
          idFonteRecurso: receita.idFonteRecurso || '',
          data: receita.data || '',
          valor: formatCurrencyInput(receita.valor || 0),
          descricao: receita.descricao || '',
        });
      } else {
        setFormData({
          ano: anoBase,
          cnpjAssociacao: cnpj,
          numConta: '',
          id: '',
          idPrograma: '',
          idFonteRecurso: '',
          data: '',
          valor: formatCurrencyInput(0),
          descricao: '',
        });
      }
      setErrors({});
      loadContas();
      loadFontesRecurso();
    }
  }, [show, receita, isEditing, cnpj, anoBase]);

  const loadContas = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL.CONTA}/all`, {
        params: { cnpjAssociacao: cnpj },
        headers: { Authorization: `Bearer ${token}` },
      });
      setContas(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar contas:', error);
    }
  };

  const carregarProgramas = async (numConta) => {
    try {
      const response = await axios.get(
        `${API_BASE_URL.CONTA_PROGRAMA}/${encodeURIComponent(cnpj)}/${encodeURIComponent(numConta)}/atual`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setProgramas(response.status === 200 && Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Erro ao carregar programas:', error);
      setProgramas([]);
    }
  };

  useEffect(() => {
    if (formData.numConta) {
      carregarProgramas(formData.numConta);
    } else {
      setProgramas([]);
    }
  }, [formData.numConta, cnpj, token]);

  const loadFontesRecurso = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL.FONTE_RECURSO}/all`, {
        params: { cnpjAssociacao: cnpj },
        headers: { Authorization: `Bearer ${token}` },
      });
      setFontesRecurso(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar fontes de recurso:', error);
    }
  };

  const handleInputChange = e => {
    const { name, value } = e.target;

    if (name === 'valor') {
      setFormData(prev => ({
        ...prev,
        [name]: formatCurrencyInput(value),
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value,
      }));
    }

    // Limpar erro do campo quando o usuário começar a digitar
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.numConta) {
      newErrors.numConta = 'Selecione uma conta';
    }
    if (!formData.idPrograma) {
      newErrors.idPrograma = 'Selecione um programa';
    }
    if (!formData.data) {
      newErrors.data = 'Informe a data da receita';
    }
    if (!formData.valor) {
      newErrors.valor = 'Informe um valor válido';
    }
    if (!formData.descricao.trim()) {
      newErrors.descricao = 'Informe a descrição da receita';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async e => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsLoading(true);

    try {
      const dataToSend = {
        ...formData,
        id: formData.id || 0,
        valor: parseCurrencyInput(formData.valor),
        idPrograma: formData.idPrograma ? parseInt(formData.idPrograma, 10) : null,
        idFonteRecurso: formData.idFonteRecurso || null,
      };

      let response;
      if (isEditing) {
        // Atualizar receita existente
        response = await axios.put(
          `${API_BASE_URL.RECEITA}/${dataToSend.ano}/${dataToSend.cnpjAssociacao}/${dataToSend.numConta}/${dataToSend.id}`,
          dataToSend,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
      } else {
        // Criar nova receita
        response = await axios.post(`${API_BASE_URL.RECEITA}`, dataToSend, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }

      // Chama callback de sucesso
      if (onSaveSuccess) {
        onSaveSuccess(response.data, isEditing);
      }

      // Fecha o modal
      handleClose();
    } catch (error) {
      console.error('Erro ao salvar receita:', error);

      // Tratar erros de validação do backend
      const backendMessage =
        typeof error.response?.data === 'string'
          ? error.response.data
          : error.response?.data?.message || error.response?.data?.error;

      if (error.response?.status === 400) {
        setErrors({
          submit: backendMessage || 'Dados inválidos. Verifique os campos.',
        });
      } else {
        setErrors({
          submit:
            backendMessage ||
            `Erro ao ${
              isEditing ? 'atualizar' : 'cadastrar'
            } receita. Tente novamente.`,
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      ano: anoBase,
      cnpjAssociacao: cnpj,
      numConta: '',
      id: '',
      idPrograma: '',
      idFonteRecurso: '',
      data: '',
      valor: formatCurrencyInput(0),
      descricao: '',
    });
    setErrors({});
    setIsLoading(false);
    onClose();
  };

  if (!show) return null;

  return (
    <div
      className="modal show d-block"
      tabIndex="-1"
      style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
    >
      <div className="modal-dialog modal-lg modal-dialog-centered">
        <div className="modal-content">
          <div
            className="modal-header bg-primary text-white"
            style={{ height: '30px' }}
          >
            <h5 className="modal-title">
              <i className="bi bi-cash-coin me-2"></i>
              {isEditing ? 'Editar Receita' : 'Nova Receita'}
            </h5>
            <button
              type="button"
              className="btn-close btn-close-white"
              onClick={handleClose}
              disabled={isLoading}
            ></button>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="modal-body small">
              {errors.submit && (
                <div className="alert alert-danger">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  {errors.submit}
                </div>
              )}

              <div className="row">
                <div className="col-md-9 mb-2">
                  <label className="form-label">
                    Conta <span className="text-danger">*</span>
                  </label>
                  <select
                    className={`form-control ${
                      errors.numConta ? 'is-invalid' : ''
                    }`}
                    name="numConta"
                    value={formData.numConta}
                    onChange={handleInputChange}
                    disabled={isLoading}
                    required
                  >
                    <option value="">Selecione uma conta...</option>
                    {contas.map(conta => (
                      <option key={conta.numConta} value={conta.numConta}>
                        {conta.numConta} {conta.nomeBanco} {conta.descricao}
                      </option>
                    ))}
                  </select>
                  {errors.numConta && (
                    <div className="invalid-feedback">{errors.numConta}</div>
                  )}
                </div>

                <div className="col-md-3 mb-2">
                  <label className="form-label">
                    Data <span className="text-danger">*</span>
                  </label>
                  <input
                    type="date"
                    className={`form-control ${
                      errors.data ? 'is-invalid' : ''
                    }`}
                    name="data"
                    value={formData.data}
                    onChange={handleInputChange}
                    disabled={isLoading}
                    required
                  />
                  {errors.data && (
                    <div className="invalid-feedback">{errors.data}</div>
                  )}
                </div>
              </div>

              <div className="row">
                <div className="col-md-6 mb-2">
                  <label className="form-label">
                    Programa <span className="text-danger">*</span>
                  </label>
                  <select
                    className={`form-control ${
                      errors.idPrograma ? 'is-invalid' : ''
                    }`}
                    name="idPrograma"
                    value={formData.idPrograma}
                    onChange={handleInputChange}
                    disabled={isLoading}
                    required
                  >
                    <option value="">Selecione um programa...</option>
                    {programas.map(p => (
                      <option key={p.idPrograma} value={p.idPrograma}>
                        {p.nomePrograma}
                      </option>
                    ))}
                  </select>
                  {errors.idPrograma && (
                    <div className="invalid-feedback">{errors.idPrograma}</div>
                  )}
                </div>

                <div className="col-md-3 mb-2">
                  <label className="form-label">
                    Valor <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className={`form-control ${
                      errors.valor ? 'is-invalid' : ''
                    }`}
                    name="valor"
                    value={formData.valor}
                    onChange={handleInputChange}
                    placeholder="R$ 0,00"
                    disabled={isLoading}
                    required
                  />
                  {errors.valor && (
                    <div className="invalid-feedback">{errors.valor}</div>
                  )}
                </div>

                <div className="col-md-9 mb-2">
                  <label className="form-label">Fonte de Recurso</label>
                  <select
                    className="form-control"
                    name="idFonteRecurso"
                    value={formData.idFonteRecurso}
                    onChange={handleInputChange}
                    disabled={isLoading}
                  >
                    <option value="">Selecione uma fonte de recurso...</option>
                    {fontesRecurso.map(fonte => (
                      <option key={fonte.id} value={fonte.id}>
                        {fonte.id} - {fonte.descricao}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="row">
                <div className="col-12 mb-2">
                  <label className="form-label">
                    Descrição <span className="text-danger">*</span>
                  </label>
                  <textarea
                    className={`form-control ${
                      errors.descricao ? 'is-invalid' : ''
                    }`}
                    name="descricao"
                    value={formData.descricao}
                    onChange={handleInputChange}
                    rows="3"
                    placeholder="Descreva a receita..."
                    disabled={isLoading}
                    required
                  />
                  {errors.descricao && (
                    <div className="invalid-feedback">{errors.descricao}</div>
                  )}
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={handleClose}
                disabled={isLoading}
              >
                <i className="bi bi-x-lg me-1" />
                Cancelar
              </button>
              <button
                type="submit"
                className="btn btn-success btn-sm"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                    />
                    Salvando...
                  </>
                ) : (
                  <>
                    <i className="bi bi-check-lg me-1" />
                    {isEditing ? 'Atualizar' : 'Salvar'}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default ReceitaModal;
